import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { SERVICES } from 'utils/service-entitlements-config';
import { LEGAL_PATHS } from 'utils/constants';
import { BRAND_CODES } from 'brand-config';
import {
  ACTIVATE_CODE_PATH,
  BREACH_INFORMATION_REDIRECTS,
} from 'services/enrollment/config';

// components
import AppErrorBoundary from 'app/error-boundary';
import Layout from 'components/Layout/Layout';
import BrandLoader from 'components/Layout/BrandLoader';
import { PageLoader } from 'components/Spin';

// route guards
import AuthRoute from './AuthRoute';
import UnAuthRoute from './UnAuthRoute';
import PrivateRoute from './PrivateRoute';
import BrandRoute from './BrandRoute';
import EbEnrollmentRoute from './EbEnrollmentRoute';
import PreserveQueryRedirect from './PreserveQueryRedirect';

// Lazy-loaded pages
const Dashboard = lazy(() => import('pages/dashboard'));
const PasswordScan = lazy(() => import('pages/password-scan'));
const ThinEnrollment = lazy(() => import('pages/enrollment/thin-enrollment'));
const FiveStepEnrollment = lazy(
  () => import('pages/enrollment/five-step-enrollment'),
);
const FlexEnrollment = lazy(() => import('pages/enrollment/flex-enrollment'));
const LightEnrollment = lazy(() => import('pages/enrollment/light-enrollment'));
const ActivateCode = lazy(() => import('pages/enrollment/activate-code'));
const AolEnrollment = lazy(() => import('pages/enrollment/aol-enrollment'));
const AolPreEnroll = lazy(
  () => import('pages/enrollment/aol-enrollment/pre-enroll'),
);
const ActivateCodeRedirect = lazy(
  () => import('pages/enrollment/activate-code/redirect'),
);
const CreatingDashboard = lazy(
  () => import('pages/enrollment/creating-dashboard'),
);
const SignIn = lazy(() => import('pages/signin'));
const ForgotPassword = lazy(() => import('pages/forgot-password'));
const ForgotUsername = lazy(() => import('pages/forgot-username'));
const PasswordResetMessage = lazy(() => import('pages/password-reset-message'));
const VerifyYourIdentity = lazy(() => import('pages/verify-your-identity'));
const ResetPasswordVerifyIdentity = lazy(
  () => import('pages/reset-password-verify-identity'),
);
const CreateNewPassword = lazy(() => import('pages/create-new-password'));
const ResetPassword = lazy(() => import('pages/reset-password'));
const Alerts = lazy(() => import('pages/alerts'));
const SingleAlert = lazy(() => import('pages/single-alert'));
const EnrollmentFAQ = lazy(() => import('pages/enrollment-faq'));
const DarkWebWatchlist = lazy(() => import('pages/dark-web-watchlist'));
const SyntheticIdentityReport = lazy(
  () => import('pages/synthetic-identity-report'),
);
const SocialMediaMonitoring = lazy(
  () => import('pages/social-media-monitoring'),
);
const DataBrokerOptOut = lazy(() => import('pages/data-broker-opt-out'));
const VpnInTouch = lazy(() => import('pages/vpn-in-touch'));
const PasswordDefense = lazy(() => import('pages/password-defense'));
const PasswordManager = lazy(() => import('pages/password-manager'));
const PasswordManagerInstructions = lazy(
  () => import('pages/password-manager/browser-instructions'),
);
const Antivirus = lazy(() => import('pages/antivirus'));
const ThankYouDownload = lazy(() => import('pages/thank-you-download'));
const IdauthThankYou = lazy(() => import('pages/idauth-thank-you'));
const IdauthSmfaRedirect = lazy(() => import('pages/idauth-smfa-redirect'));
const Passwords = lazy(() => import('pages/vault/passwords'));
const EmailVerification = lazy(() => import('pages/email-verification'));
const Support = lazy(() => import('pages/support'));
const DigitalVault = lazy(() => import('pages/vault/digital-vault'));
const DigitalVaultLayout = lazy(
  () => import('pages/vault/digital-vault/layout'),
);
const DocumentDetails = lazy(
  () => import('pages/vault/digital-vault/partials/document-details'),
);
const MyAccount = lazy(() => import('pages/myaccount'));
const PlanMembersPage = lazy(() => import('pages/myaccount/plan-members'));
const MemberProtectionDetailsPage = lazy(
  () => import('pages/myaccount/plan-members/member-protection-details'),
);
const MyPlan = lazy(() => import('pages/myplan'));
const OffersAndUpgrades = lazy(() => import('pages/offers-and-upgrades'));
const FAQS = lazy(() => import('pages/faqs'));
const AccountDetails = lazy(() => import('pages/account-details'));
const AccountDetailsView = lazy(
  () => import('pages/vault/passwords/partials/accountDetailsView'),
);
const NotificationSettings = lazy(() => import('pages/notification-settings'));
const TopicFAQ = lazy(() => import('pages/faqs/partials/topic-faq'));
const LegacyFaqRedirect = lazy(
  () => import('pages/faqs/partials/legacy-faq-redirect'),
);
const VaultDetails = lazy(
  () => import('pages/myaccount/partials/vault-details'),
);
const TermsOfService = lazy(() => import('pages/terms-of-service'));
const PrivacyPolicy = lazy(() => import('pages/privacy-policy'));
const WebsiteTermsOfUse = lazy(
  () => import('pages/terms-of-service/website-terms-of-use'),
);
const RefundPolicy = lazy(() => import('pages/privacy-policy/refund-policy'));
const IdTheftProtection = lazy(() => import('pages/privacy-policy/id-theft'));
const PartnerTermsOfService = lazy(
  () => import('pages/terms-of-service/partner'),
);
const PartnerPrivacyPolicy = lazy(() => import('pages/privacy-policy/partner'));
const CompanyCode = lazy(
  () => import('pages/enrollment/activate-code/company-code'),
);
const DownloadPasswordDefense = lazy(
  () => import('pages/password-defense/partials/download-password-defense'),
);
const Insurance = lazy(() => import('pages/insurance'));
const HomeChoiceFaq = lazy(() => import('pages/home-choice-faq'));
const WalletProtection = lazy(() => import('pages/wallet-protection'));
const MedicalDataMonitoring = lazy(
  () => import('pages/medical-data-monitoring'),
);
const SecureDocumentUpload = lazy(() => import('pages/secure-document-upload'));
const Security = lazy(() => import('pages/security'));
const HomeTitleMonitoring = lazy(() => import('pages/home-title-monitoring'));
const HtmonRegisteredPropertyDetails = lazy(
  () => import('pages/home-title-monitoring/registered-property-details'),
);
const PublicRecordsMonitoring = lazy(
  () => import('pages/public-records-monitoring'),
);
const PublicRecordsPropertyDetails = lazy(
  () => import('pages/public-records-monitoring/property-details'),
);
const PublicRecordTrace = lazy(() => import('pages/public-record-trace'));
const TransactionMonitoringIndex = lazy(
  () => import('pages/transaction-monitoring'),
);
const TransactionHistoryPage = lazy(
  () => import('modules/transaction-monitoring/pages/TransactionHistoryPage'),
);
const YourAccountsPage = lazy(
  () => import('modules/transaction-monitoring/pages/YourAccountsPage'),
);
const VerifyOAuthPage = lazy(
  () => import('modules/transaction-monitoring/pages/VerifyOAuthPage'),
);
const CreditScore = lazy(() => import('pages/credit-score'));
const CreditScoreTracker = lazy(() => import('pages/credit-score-tracker'));
const CreditScoreSimulator = lazy(() => import('pages/credit-score-simulator'));
const CreditLock = lazy(() => import('pages/credit-lock'));
const SecurityFreeze = lazy(() => import('pages/security-freeze'));
const CreditReportIndex = lazy(() => import('pages/credit-report'));
const CreditReportBureauIndex = lazy(
  () => import('pages/credit-report/CreditReportBureauIndex'),
);
const BankAccountMonitoring = lazy(
  () => import('pages/bank-account-monitoring'),
);
const IdentityMonitoring = lazy(() => import('pages/identity-monitoring'));
const FinancialCalculatorPage = lazy(
  () => import('pages/financial-calculator'),
);
const FinancialCalculatorDetailPage = lazy(
  () => import('pages/financial-calculator/detail'),
);
const SpendingTrackerPage = lazy(() => import('pages/spending-tracker'));
const InsightsAndTipsPage = lazy(() => import('pages/insights-and-tips'));
const InsightsAndTipsArticlePage = lazy(
  () => import('pages/insights-and-tips/article-detail'),
);

/* Error pages */
const ErrorPageLayout = lazy(() => import('pages/error-page'));
const SomethingWentWrong = lazy(
  () => import('pages/error-page/something-went-wrong'),
);
const OfferNotFound = lazy(() => import('pages/error-page/offer-not-found'));
const BrandNotMatched = lazy(
  () => import('pages/error-page/brand-not-matched'),
);
const BrandNotValid = lazy(() => import('pages/error-page/brand-not-valid'));
const IdauthStatusDeny = lazy(
  () => import('pages/error-page/idauth-status-deny'),
);
const IdentityNotVerified = lazy(
  () => import('pages/error-page/identity-not-verified'),
);
/* Family-plan invitation failures, keyed off the participant-token lookup's status. */
const InvalidParticipantToken = lazy(
  () => import('pages/error-page/invalid-participant-token'),
);
const ServiceEndRequest = lazy(
  () => import('pages/error-page/service-end-request'),
);
const InvitationWithdrawn = lazy(
  () => import('pages/error-page/invitation-withdrawn'),
);
const AlreadyUsed = lazy(() => import('pages/error-page/already-used'));
const TechnicalDifficulties = lazy(
  () => import('pages/error-page/technical-difficulties'),
);

const routes = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        element: <AuthRoute />,
        children: [
          {
            index: true,
            element: <Navigate to='dashboard' replace />,
          },
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: 'alerts',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.alerts}>
                  <Alerts />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Email deep-link parity with Angular
            // (`app-routing.module.ts` `single-alert/:alertId`
            // → `user/all/alert/type/all/alert/:alertId`). Standalone
            // deep-link page using SingleAlertHeader + PanelContent
            // `variant="page"`. Gated on the alerts entitlement so an unentitled user
            // gets the standard PrivateRoute bounce instead of a
            // dashboard fallback.
            path: 'single-alert/:alertId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.alerts}>
                  <SingleAlert />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Child-scoped alerts (Figma 1:6475). Same component; the
            // page reads `:childId` from useParams and toggles into
            // child mode (breadcrumbs + `{first}'s alerts` title + the
            // alerts fetch's `userIds` query param).
            //
            // Gated on `familyPlan` (not `alerts`) because MinorDefense
            // parents have `can_invite` but not the primary-user alerts
            // entitlements — they still need to see their child's alerts.
            path: 'alerts/child/:childId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.familyPlan}>
                  <Alerts />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'vpn-in-touch',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.vpnInTouch}>
                  <VpnInTouch />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'security',
            children: [
              {
                path: 'antivirus',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.antivirus}>
                      <Antivirus />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'passwords',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.passwords}>
                      <Passwords />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.passwords}>
                      <AccountDetailsView />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'digital-vault',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.digitalVault}>
                  <DigitalVaultLayout />
                </PrivateRoute>
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DigitalVault />
                  </Suspense>
                ),
              },
              {
                path: ':id',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <DocumentDetails />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'my-account',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MyAccount />
                  </Suspense>
                ),
              },
              {
                path: 'plan-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <MyPlan />
                  </Suspense>
                ),
              },
              {
                path: 'offers-and-upgrades',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <OffersAndUpgrades />
                  </Suspense>
                ),
              },
              {
                path: 'account-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AccountDetails />
                  </Suspense>
                ),
              },
              {
                path: 'notification-settings',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <NotificationSettings />
                  </Suspense>
                ),
              },
              {
                path: 'vault-details',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.digitalVault}>
                      <VaultDetails />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: 'plan-members',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.familyPlan}>
                      <PlanMembersPage />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: 'plan-members/:childId',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.familyPlan}>
                      <MemberProtectionDetailsPage />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: 'secure-document-upload',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SecureDocumentUpload />
                  </Suspense>
                ),
              },
              {
                path: 'security',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Security />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'support',
            element: (
              <Suspense fallback={<PageLoader />}>
                <Support />
              </Suspense>
            ),
          },
          {
            path: 'password-scan',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.passwordScan}>
                  <PasswordScan />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'medical-data-monitoring',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.medicalDataMonitoring}>
                  <MedicalDataMonitoring />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'dark-web',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.darkweb}>
                  <DarkWebWatchlist />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Child-scoped Dark Web view. Same top-level component
            // forks by `useParams().childId` — read-only list of the
            // child's monitored items sourced from state.childItems
            // (populated by `/api/client/monitored_items/dependents/children`).
            //
            // Gated on `familyPlan` (not `darkweb`) so MinorDefense
            // parents — who have `can_invite` but not the primary-user
            // darkweb entitlement — can still view their child's list.
            path: 'dark-web/child/:childId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.familyPlan}>
                  <DarkWebWatchlist />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Synthetic Identity Report — child-only feature, so there is
            // deliberately no bare `/synthetic-identity-report` route and no
            // nav or dashboard entry (BR-003a / BR-003b). Angular's bare route
            // redirected to the first child and had a "no members" state; both
            // are dropped, since React can only reach this page from a child
            // that already exists.
            //
            // Gated on `familyPlan` (not an SSNT entitlement) so MinorDefense
            // parents — who have `can_invite` but not the primary-user
            // entitlements — can still view their child's report. BR-001e:
            // `ssn_trace` is never derived in React.
            path: 'synthetic-identity-report/:childId',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.familyPlan}>
                  <SyntheticIdentityReport />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Public Record Trace — Minor Defense only. Gated on the
            // synthesized `publicRecordTrace` service; the synthesis
            // lives in `selectUserFeatures` (dminor brand +
            // `itps:ssn_trace` includes 'ssnt') so `<PrivateRoute
            // service=...>` here does the entire enforcement.
            // Angular parity — `identity/public-record-trace` route
            // with `@Feature(ClientFeature.public_record_trace)`.
            path: 'public-record-trace',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.publicRecordTrace}>
                  <PublicRecordTrace />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'social-media-monitoring',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.socialMedia}>
                      <SocialMediaMonitoring />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                // Child-scoped social-media view. Gated on `familyPlan`
                // (not `socialMedia`) so MinorDefense parents — who
                // have `can_invite` but not the primary-user socmed
                // entitlement — can still view / connect their child's
                // accounts.
                path: ':childId',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.familyPlan}>
                      <SocialMediaMonitoring />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'data-broker-opt-out',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.dboo}>
                  <DataBrokerOptOut />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'password-defense',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute
                  service={SERVICES.passwords}
                  agentCheckRequired={false}
                >
                  <PasswordDefense />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'security/password-manager',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.passwordManager}>
                  <PasswordManager />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'security/password-manager/:browser',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.passwordManager}>
                  <PasswordManagerInstructions />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'insurance',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.insurance}>
                  <Insurance />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'financial',
            children: [
              {
                path: 'new-home-title-monitoring',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.htmon}>
                          <HomeTitleMonitoring />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: ':addressId',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.htmon}>
                          <HtmonRegisteredPropertyDetails />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'home-title-monitoring',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.publicRecords}>
                          <PublicRecordsMonitoring />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: ':id',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.publicRecords}>
                          <PublicRecordsPropertyDetails />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'financial-calculators',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.financialCalculator}>
                          <FinancialCalculatorPage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: ':code',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.financialCalculator}>
                          <FinancialCalculatorDetailPage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'spending',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.spendingTracker}>
                      <SpendingTrackerPage />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: 'transaction-monitoring',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.txmon}>
                          <TransactionMonitoringIndex />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: 'transaction-history',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.txmon}>
                          <TransactionHistoryPage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: 'your-accounts',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.txmon}>
                          <YourAccountsPage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'credit-score',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditScore}>
                  <CreditScore />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'credit-tracker',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditScore}>
                  <CreditScoreTracker />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'credit-lock',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.creditLock}>
                  <CreditLock />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'wallet-protection',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.walletProtection}>
                  <WalletProtection />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'resources',
            children: [
              {
                // Angular parity — the Help page lived at `/resources/contact-us`.
                path: 'contact-us',
                element: <PreserveQueryRedirect to='/support' />,
              },
              {
                // Angular parity — topic FAQs lived at `/resources/faqs/<slug>`.
                path: 'faqs',
                children: [
                  {
                    index: true,
                    element: <PreserveQueryRedirect to='/faqs' />,
                  },
                  {
                    path: ':slug',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <LegacyFaqRedirect />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'security-freeze',
                children: [
                  {
                    path: 'home',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.securityFreeze}>
                          <SecurityFreeze />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'insights-and-tips',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.insightsAndTips}>
                          <InsightsAndTipsPage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                  {
                    path: ':title',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <PrivateRoute service={SERVICES.insightsAndTips}>
                          <InsightsAndTipsArticlePage />
                        </PrivateRoute>
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'credit',
            children: [
              {
                path: 'score-simulator',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.creditScoreSimulator}>
                      <CreditScoreSimulator />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'credit-report',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.creditReport}>
                      <CreditReportIndex />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
              {
                path: ':bureauId',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PrivateRoute service={SERVICES.creditReport}>
                      <CreditReportBureauIndex />
                    </PrivateRoute>
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'bank-account-monitoring',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.bankAccountMonitoring}>
                  <BankAccountMonitoring />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            // Angular parity — `/identity/identity-monitoring`
            // (`app-routing.module.ts:1106`). Gated on the
            // `authentication_alerts` entitlement via
            // `SERVICES.identityMonitoring`.
            path: 'identity/identity-monitoring',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivateRoute service={SERVICES.identityMonitoring}>
                  <IdentityMonitoring />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: 'faqs',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FAQS />
                  </Suspense>
                ),
              },
              {
                path: ':topic',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TopicFAQ />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'verify-oauth',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VerifyOAuthPage />
              </Suspense>
            ),
          },
          {
            path: '*',
            element: <Navigate to='dashboard' replace />,
          },
        ],
      },
      {
        element: <UnAuthRoute />,
        children: [
          {
            path: 'enrollment',
            children: [
              {
                /* Nothing enrols from the bare module path — it is a short link onto the redeem
                   page, exactly as /enrollment/:client and /activate[/:client] below are. */
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ActivateCodeRedirect />
                  </Suspense>
                ),
              },
              {
                /* identityWeb's classic multi-step flow, and the destination for every redeemed
                   code and paid deep link that is neither thin nor flex. */
                path: 'general-info',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FiveStepEnrollment />
                  </Suspense>
                ),
              },
              {
                path: 'thin',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ThinEnrollment />
                  </Suspense>
                ),
              },
              {
                path: 'flex',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <FlexEnrollment />
                  </Suspense>
                ),
              },
              {
                path: 'light',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <LightEnrollment />
                  </Suspense>
                ),
              },
              {
                /* Where every flow above ends: the scan animation and the auto sign-in. */
                path: 'creating-dashboard',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <CreatingDashboard />
                  </Suspense>
                ),
              },
              {
                /* The landing route for every non-paid enrollment: redeem a code here, then the
                   brand and the offer decide which flow above the member lands in. */
                path: 'activate',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <ActivateCode />
                      </Suspense>
                    ),
                  },
                  {
                    /* The company/settlement landing. CompanyCode owns the company lookup and
                       either renders ActivateCode in place or sends the client to light
                       enrollment. */
                    path: ':client',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <CompanyCode />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                /* Home Title Guardian's single-page enrollment. The token was already checked by
                   the pre-enroll screen, which is what forwards the member here. */
                path: 'aol',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BrandRoute brandCodes={[BRAND_CODES.homeTitleGuardian]}>
                      <AolEnrollment />
                    </BrandRoute>
                  </Suspense>
                ),
              },
              {
                /* The same screen for Financial Shield's partner offers, which arrive with a
                   token this screen checks itself. */
                path: 'partner',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BrandRoute brandCodes={[BRAND_CODES.financialShield]}>
                      <AolEnrollment />
                    </BrandRoute>
                  </Suspense>
                ),
              },
              {
                /* A company short link. Ranked below every static segment above, so it only
                   catches what none of them matched. */
                path: ':client',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ActivateCodeRedirect />
                  </Suspense>
                ),
              },
            ],
          },
          /* identityWeb exposes the redeem page at /activate too; keep the short links
             working by redirecting onto the canonical enrollment path. */
          {
            path: 'activate',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ActivateCodeRedirect />
                  </Suspense>
                ),
              },
              {
                path: ':client',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ActivateCodeRedirect />
                  </Suspense>
                ),
              },
            ],
          },
          /* identityWeb mounts the enrollment module a second time at eb/enrollment behind
             EmployeeBenefitsGuard (app-routing.module.ts:230-236). There is one enrollment entry
             point here, so the alias is a guarded redirect onto it. */
          {
            path: 'eb/enrollment',
            element: <EbEnrollmentRoute />,
          },
          /* Where AOL lands a Home Title Guardian member before enrollment
             (app-routing.module.ts:404-407). Outside /enrollment, so it is registered here. */
          {
            path: 'aol/PreEnroll/AOLRequest',
            element: (
              <Suspense fallback={<PageLoader />}>
                <BrandRoute brandCodes={[BRAND_CODES.homeTitleGuardian]}>
                  <AolPreEnroll />
                </BrandRoute>
              </Suspense>
            ),
          },
          /* Breach-programme short links. identityWeb redirects each onto the redeem page
             (app-routing.module.ts:314-395); two of them rename the client on the way, so a
             ':client' catch-all cannot serve them. The FinancialShieldBrandGuard those routes
             carry never runs — Angular resolves redirectTo while matching the URL, before
             canActivate — so these are plain redirects. */
          {
            path: 'information',
            children: BREACH_INFORMATION_REDIRECTS.map(({ path, client }) => ({
              path,
              element: (
                <PreserveQueryRedirect to={`${ACTIVATE_CODE_PATH}/${client}`} />
              ),
            })),
          },
          {
            path: 'sign-in',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SignIn />
                  </Suspense>
                ),
              },
              {
                // Angular parity — `/sign-in/email-form` (and legacy
                // `/sign-in/forgot-password`) is where reset-password
                // emails and older bookmarks land. React consolidated
                // this screen at the top-level `/forgot-password` route.
                path: 'email-form',
                element: <Navigate to='/forgot-password' replace />,
              },
              {
                // Angular parity — the account-lockout alert on
                // /sign-in offers a "Reset Your Password" link that
                // deep-links here to confirm an email has been sent.
                path: 'password-reset-message',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <PasswordResetMessage />
                  </Suspense>
                ),
              },
              {
                // Angular parity — `/sign-in/verify-user` renders the
                // identity form after token validation (ForgotPasswordComponent).
                path: 'verify-user',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ResetPasswordVerifyIdentity />
                  </Suspense>
                ),
              },
              {
                // `/sign-in/set-password?token=<jwt>` — the URL every
                // reset-password email opens. Routed to the new
                // reset-password-verify-identity screen (Angular
                // parity — validates the token, then shows the
                // identity-verification form before letting the user
                // set a new password).
                path: 'set-password',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ResetPasswordVerifyIdentity />
                  </Suspense>
                ),
              },
              {
                path: 'forgot-username',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ForgotUsername />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'forgot-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            ),
          },
          {
            path: 'verify-identity',
            element: (
              <Suspense fallback={<PageLoader />}>
                <VerifyYourIdentity />
              </Suspense>
            ),
          },
          {
            path: 'reset-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            ),
          },
          {
            path: 'create-password',
            element: (
              <Suspense fallback={<PageLoader />}>
                <CreateNewPassword />
              </Suspense>
            ),
          },
          {
            path: 'faq',
            element: (
              <Suspense fallback={<PageLoader />}>
                <EnrollmentFAQ />
              </Suspense>
            ),
          },
          {
            path: 'error-page',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ErrorPageLayout />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <Navigate to='something-went-wrong' replace />
                  </Suspense>
                ),
              },
              {
                path: 'something-went-wrong',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <SomethingWentWrong />
                  </Suspense>
                ),
              },
              {
                path: 'offer-not-found',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <OfferNotFound />
                  </Suspense>
                ),
              },
              {
                path: 'brand-not-matched',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BrandNotMatched />
                  </Suspense>
                ),
              },
              {
                path: 'brand-not-valid',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <BrandNotValid />
                  </Suspense>
                ),
              },
              {
                path: 'idauth-status-deny',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <IdauthStatusDeny />
                  </Suspense>
                ),
              },
              {
                path: 'identity-not-verified',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <IdentityNotVerified />
                  </Suspense>
                ),
              },
              {
                path: 'invalid-participant-token',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <InvalidParticipantToken />
                  </Suspense>
                ),
              },
              {
                path: 'service-end-request',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <ServiceEndRequest />
                  </Suspense>
                ),
              },
              {
                path: 'invitation-withdrawn',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <InvitationWithdrawn />
                  </Suspense>
                ),
              },
              {
                path: 'already-used',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <AlreadyUsed />
                  </Suspense>
                ),
              },
              {
                path: 'technical-difficulties',
                element: (
                  <Suspense fallback={<PageLoader />}>
                    <TechnicalDifficulties />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to='sign-in' replace />,
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: <BrandLoader />,
    errorElement: <AppErrorBoundary />,
    children: [
      {
        path: 'thank-you-download',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ThankYouDownload />
          </Suspense>
        ),
      },
      {
        path: 'thank-you',
        element: (
          <Suspense fallback={<PageLoader />}>
            <IdauthThankYou />
          </Suspense>
        ),
      },
      {
        path: 'verify/:authId',
        element: (
          <Suspense fallback={<PageLoader />}>
            <IdauthSmfaRedirect />
          </Suspense>
        ),
      },
      {
        path: 'dw-email-verification',
        element: (
          <Suspense fallback={<PageLoader />}>
            <EmailVerification />
          </Suspense>
        ),
      },
      /* `/terms-of-service` and `/privacy-policy` stay the only canonical legal paths.
         The documents Angular serves alongside them are child routes, so the two entry
         points do not multiply into five. Each parent is pathless-by-children — no
         `element` of its own — so React Router renders the matched child directly and a
         child cannot end up nested inside the canonical document. */
      {
        path: 'terms-of-service',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <TermsOfService />
              </Suspense>
            ),
          },
          {
            path: 'website-terms-of-use',
            element: (
              <Suspense fallback={<PageLoader />}>
                <WebsiteTermsOfUse />
              </Suspense>
            ),
          },
          {
            path: 'partner',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PartnerTermsOfService />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'privacy-policy',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <PrivacyPolicy />
              </Suspense>
            ),
          },
          {
            path: 'refund-policy',
            element: (
              <Suspense fallback={<PageLoader />}>
                <RefundPolicy />
              </Suspense>
            ),
          },
          {
            path: 'id-theft',
            element: (
              <Suspense fallback={<PageLoader />}>
                <IdTheftProtection />
              </Suspense>
            ),
          },
          {
            path: 'partner',
            element: (
              <Suspense fallback={<PageLoader />}>
                <PartnerPrivacyPolicy />
              </Suspense>
            ),
          },
        ],
      },
      /* identityWeb served its legal pages from ~15 paths. Every bookmark, emailed link
         and CMS-authored cross-reference to one of them must keep resolving, so each
         redirects to the document it showed in Angular — via `PreserveQueryRedirect` so
         `?brandDomain=` and offer params survive the hop.

         Note `policy/site-terms` and `policy/terms` both chained through Angular's
         `→ terms/website`, so they land on the *Website* terms, not the service terms. */
      {
        path: 'terms',
        children: [
          {
            index: true,
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          {
            path: 'website',
            element: (
              <PreserveQueryRedirect to={LEGAL_PATHS.websiteTermsOfUse} />
            ),
          },
          {
            path: 'service',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          {
            path: 'tou',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          {
            /* Angular's `terms/:type` rendered `idg-subbrand-terms` for this one type, for
               any brand — the offer-group document, not the brand's own. */
            path: 'service-terms',
            element: (
              <PreserveQueryRedirect to={LEGAL_PATHS.partnerTermsOfService} />
            ),
          },
          {
            path: 'service-terms-of-use',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          /* Declared last: Angular had a `terms/:type` catch-all onto the same page. */
          {
            path: ':type',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
        ],
      },
      {
        path: 'policy',
        children: [
          {
            index: true,
            element: <PreserveQueryRedirect to={LEGAL_PATHS.privacyPolicy} />,
          },
          {
            path: 'privacy',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.privacyPolicy} />,
          },
          {
            /* The `policy/:type` twin of `terms/service-terms` — `idg-subbrand-privacy`. */
            path: 'privacy-policy',
            element: (
              <PreserveQueryRedirect to={LEGAL_PATHS.partnerPrivacyPolicy} />
            ),
          },
          {
            path: 'refund',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.refundPolicy} />,
          },
          {
            path: 'id-theft',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.idTheft} />,
          },
          {
            path: 'site-terms',
            element: (
              <PreserveQueryRedirect to={LEGAL_PATHS.websiteTermsOfUse} />
            ),
          },
          {
            path: 'terms',
            element: (
              <PreserveQueryRedirect to={LEGAL_PATHS.websiteTermsOfUse} />
            ),
          },
          {
            path: 'service',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          {
            path: 'service-terms',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
          },
          {
            path: 'insurance',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.insurance} />,
          },
          {
            path: ':type',
            element: <PreserveQueryRedirect to={LEGAL_PATHS.privacyPolicy} />,
          },
        ],
      },
      {
        path: 'service-terms-of-use',
        element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
      },
      {
        path: 'legal/terms-of-service',
        element: <PreserveQueryRedirect to={LEGAL_PATHS.termsOfService} />,
      },
      {
        path: 'insurancepolicy',
        element: <PreserveQueryRedirect to={LEGAL_PATHS.insurance} />,
      },
      {
        path: 'enrollment-faqs',
        element: <PreserveQueryRedirect to={LEGAL_PATHS.faq} />,
      },
      {
        path: 'download-password-defense',
        element: (
          <Suspense fallback={<PageLoader />}>
            <DownloadPasswordDefense />
          </Suspense>
        ),
      },
      {
        /* Financial Shield's public marketing FAQ. Outside UnAuthRoute and AuthRoute, like
           identityWeb, so signed-in members can open it too. */
        path: 'home-choice-faq',
        element: (
          <Suspense fallback={<PageLoader />}>
            <BrandRoute brandCodes={[BRAND_CODES.financialShield]}>
              <HomeChoiceFaq />
            </BrandRoute>
          </Suspense>
        ),
      },
    ],
  },
]);

export default routes;
